
import React, { useEffect, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import queryString from 'query-string';

import AuthButton from 'components/base/AuthButton';
import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import CustomListComponent from 'components/base/CustomListComponent';
import DeletePrompt from 'components/base/DeletePrompt';
import CouponsOverview, { TicketsOverview } from 'components/coupon/couponList/CouponsOverview';
import DeactiveCouponSetView from 'components/coupon/couponList/DeactiveCouponSetView';
import DateRangeCompare from 'components/dashboard/DateRangeCompare';
import DateRangeSelection from 'components/dashboard/DateRangeSelection';
import { COUPON_TYPES, DELETE_RELATED_SECTIONS, StatusTag } from '../../../config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import BaseListContainer from 'containers/base/BaseListContainer';
import BaseTabListContainer from 'containers/base/BaseTabListContainer';
import { COUPON_STATUS } from 'models/CouponListModel';
import Filter from './Filter';

import { createAction, getHashKeyString } from 'utils';

import { NavBarNames } from 'config/NavBarNameList';

export const TAB_OVERVIEW = 'Overview';
export const TAB_LIST_OF_TICKET_SETS = 'List of Ticket Sets';


const mapPropsToState = (state) => ({
  totalCount: state.couponList.totalCount,
  pageInfo: state.couponList.pageInfo,
  totalPage: state.couponList.totalPage,
  displayFields: state.couponList.ticketListDisplayFields,
  couponSetList: state.couponList.currentPageCouponSetList,
  checkedList: state.couponList.checkedList,
  isSuperuser: state.users.isSuperuser,
  userPermissions: state.users.userPermissions,
  hasDashboardPermission: state.users.hasDashboardPermission,
});
const TicketsList = ({
  totalCount,
  pageInfo,
  totalPage,
  displayFields,
  couponSetList,
  checkedList,
  isSuperuser,
  userPermissions,
  hasDashboardPermission,
}) => {

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const [showDeleteView, setShowDeleteView] = useState(false);
  const [showDeactiveView, setShowDeactiveView] = useState(false);
  const [operationItem, setOperationItem] = useState();
  const activeTab = getHashKeyString(location?.hash) || (hasDashboardPermission ? TAB_OVERVIEW : TAB_LIST_OF_TICKET_SETS);
  const hasTicketPermission = isSuperuser || userPermissions.some(
    (val) => val.split('_')?.[1] === PermissionCodes.coupontemplate,
  );

  const activeOrDeactiveAction = (ticketSet) => {
    let isForcedInactive = true;
    if (ticketSet.status === COUPON_STATUS.INACTIVE) {
      isForcedInactive = false;
    }
    dispatch(
      createAction('couponList/updateCouponSetActiveStatus')({
        couponSetPK: ticketSet.pk,
        isForcedInactive,
        afterAction: () => history.push(location),
      }),
    );
  };

  const actionList = [
    {
      name: 'Delete',
      action: () => setShowDeleteView(true),
      requires: PermissionCodes.deleteCouponTemplate,
    },
  ];
  const tabs = [
    hasDashboardPermission ? { name: TAB_OVERVIEW, content: <TicketsOverview /> } : {},
    hasTicketPermission ? {
      name: TAB_LIST_OF_TICKET_SETS,
      content: (
        <BaseTabListContainer
          hideTab={true}
          groupActions={actionList}
          pageInfo={pageInfo}
          totalCount={totalCount || 0}
          filter={{ hasFilter: true, component: Filter }}
          listContent={
            <BaseListContainer
              fields={displayFields}
              model={'couponList'}
              permissionGroup={PermissionCodes.coupontemplate}
              dataList={couponSetList}
              totalPage={totalPage}
              deleteInfo={{
                data: [],
                title: 'Ticket Set',
                relatedName: '',
                onComfirm: {},
                relatedSections: DELETE_RELATED_SECTIONS.TICKET_SET,
              }}
              actions={['Edit', 'Detail', 'Delete', 'Duplicate']}
              hasOtherActions={true}
              customActions={{
                custom: (item) => {
                  if (item?.status !== 'Expired') {
                    return {
                      name: [StatusTag.active, StatusTag.activeRequired].includes(item.status) 
                        ? 'Deactive' : 'Active',
                      action: (item) => {
                        dispatch(
                          createAction('couponList/updateState')({
                            couponSet: item,
                          }),
                        );
                        if (item.status === COUPON_STATUS.ACTIVE) {
                          setShowDeactiveView(true);
                          setOperationItem(item);
                        } else if (item.status === COUPON_STATUS.INACTIVE) {
                          activeOrDeactiveAction(item);
                        }
                      },
                      requires: PermissionCodes.changeCouponTemplate,
                    };
                  }
                },
              }}
            />
          }
        />
      ),
    }: {},
  ];

  const buttons = [
    <AuthButton
      title="Create Ticket Set"
      action={() => {
        history.push('/tickets/create');
        dispatch(createAction('createCoupon/clearState')());
      }}
      requires={PermissionCodes.addCouponTemplate}
    />,
  ];

    useEffect(() => {
      if (!hasTicketPermission) { 
        return 
      }
      const searchDict = queryString.parse(location.search);
      const searchKey = searchDict['search'];
      const stringRank = searchDict['rank'] || 'true';
      const stringPage = searchDict['page'] || 1;
      const rank = stringRank === 'true';
      const page = parseInt(stringPage);
      const type = COUPON_TYPES.TICKET;
  
      dispatch(
        createAction('couponList/getCurrentPageTemplateList')({
          ...searchDict,
          type,
          rank: rank,
          filterName: searchKey,
          page: page,
        }),
      );
      return () => dispatch(createAction('couponList/clearState')());
    }, [dispatch, location]);

  
  return <>
    <CustomListComponent
      breadcrumb={<CustomBreadcrumb />}
      buttons={buttons}
      tabs={tabs}
      defaultActiveKey={activeTab}
      title={NavBarNames.tickets}
      onTabChange={(key) => {
        history.push({ pathname: location.pathname, hash: key });
      }}
      renderCompare={() => {
        return activeTab === TAB_OVERVIEW ? (
          <DateRangeCompare />
        ) : null;
      }}
      renderTabsRight={() => {
        return activeTab === TAB_OVERVIEW ? (
          <DateRangeSelection
            onTab
            applyDateRangeEvent={(startDate, endDate) => {
              dispatch(
                createAction('overview/getTickData')({
                  startDate: startDate.format('YYYY-MM-DD'),
                  endDate: endDate.format('YYYY-MM-DD'),
                }),
              );
            }}
            clearDataEvent={() => {
              dispatch(createAction('overview/clearState')());
            }}
          />
        ) : null;
      }}
    />

    <DeletePrompt
      data={checkedList}
      show={showDeleteView}
      title={'Ticket Set'}
      relatedName=""
      relatedSections={DELETE_RELATED_SECTIONS.TICKET_SET}
      onClose={() => setShowDeleteView(false)}
      onConfirm={() => {
        setShowDeleteView(false);
        dispatch(
          createAction('couponList/delete')({
            afterAction: () => history.push(location),
          }),
        );
      }}
    />

    <DeactiveCouponSetView
      couponSet={operationItem}
      showDeactiveView={showDeactiveView}
      onHide={() => setShowDeactiveView(false)}
      deactiveAction={() => {
        activeOrDeactiveAction(operationItem);
      }}
      isTicket
    />
  </>
}



export default connect(mapPropsToState)(TicketsList);